'use client';

import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { CompanySearchResult } from '@/lib/actions/company-search';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FIELD_LABEL_CLASS, INPUT_PLACEHOLDER_CLASS } from '../FilterControls';
import {
  handleKeywordKeyDown,
  RecentSearchesDropdown,
  SuggestionsDropdown,
} from './KeywordSearchFieldParts';

export interface KeywordSearchFieldProps {
  autocompleteRef: RefObject<HTMLDivElement | null>;
  t: TFunction<'translation', undefined>;
  searchInput: string;
  setSearchInput: Dispatch<SetStateAction<string>>;
  setShowLastSearches: Dispatch<SetStateAction<boolean>>;
  fetchSuggestions: (query: string) => void;
  setShowSuggestions: Dispatch<SetStateAction<boolean>>;
  fetchLastSearches: () => Promise<string[]>;
  showSuggestions: boolean;
  suggestions: CompanySearchResult[];
  keyboardNavRef: RefObject<boolean>;
  setSidebarActiveIndex: Dispatch<SetStateAction<number>>;
  sidebarActiveIndex: number;
  handleSuggestionClick: (suggestion: CompanySearchResult) => void;
  handleFormSubmit: () => void;
  showLastSearches: boolean;
  lastSearches: string[];
  suggestionsLoading: boolean;
  discardLastSearch: (keyword: string) => Promise<void>;
}

export function KeywordSearchField(props: KeywordSearchFieldProps) {
  const {
    autocompleteRef,
    t,
    searchInput,
    setSearchInput,
    setShowLastSearches,
    fetchSuggestions,
    setShowSuggestions,
    fetchLastSearches,
    showSuggestions,
    suggestions,
    showLastSearches,
    lastSearches,
  } = props;
  return (
    <div className="space-y-1.5 mb-3" ref={autocompleteRef}>
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.description')}
      </Label>
      <div className="relative">
        <Input
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            if (e.target.value.trim().length >= 3) {
              setShowLastSearches(false);
              fetchSuggestions(e.target.value);
            } else if (e.target.value.length === 0) {
              setShowSuggestions(false);
              fetchLastSearches().then((keywords) => {
                if (keywords.length > 0) setShowLastSearches(true);
              });
            } else {
              setShowSuggestions(false);
              setShowLastSearches(false);
            }
          }}
          onKeyDown={(e) => handleKeywordKeyDown(e, props)}
          onFocus={() => {
            if (searchInput.trim().length >= 3) {
              if (suggestions.length > 0) setShowSuggestions(true);
            } else if (!searchInput || searchInput.length === 0) {
              fetchLastSearches().then((keywords) => {
                if (keywords.length > 0) setShowLastSearches(true);
              });
            }
          }}
          onClick={() => {
            if (!searchInput || searchInput.length === 0) {
              fetchLastSearches().then((keywords) => {
                if (keywords.length > 0) setShowLastSearches(true);
              });
            }
          }}
          placeholder={t('search.sidebar.descriptionPlaceholder')}
          className={`h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
        />
        {showSuggestions && searchInput.trim().length >= 3 && (
          <SuggestionsDropdown {...props} />
        )}
        {showLastSearches && !showSuggestions && lastSearches.length > 0 && (
          <RecentSearchesDropdown {...props} />
        )}
      </div>
    </div>
  );
}
